/**
 * eventer creates a tiny typed event channel with .emit() and .on() methods.
 *
 * Obs! Replay event is not supported.
 */
export const createEventerFn =
  (
    target: EventTarget = new EventTarget(),
    eventName: string,
    options?: { log?: boolean; logger?: (msg: string, ...args: any[]) => void; isDev: boolean }
  ) =>
  <P = undefined>() => {
    const log = options?.log;
    const logger = options?.logger ?? console.log;
    const isDev = options?.isDev ?? false;

    const emit = (...args: P extends undefined ? [] | [P] : [P]) => {
      const payload = (args[0] as P) ?? (undefined as P);

      if (isDev && log) {
        logger(`⚡ eventer → emit: ${eventName}`, payload ? payload : '(no payload)');
      }

      const event = new CustomEvent<P>(eventName, { detail: payload });

      target.dispatchEvent(event);
    };

    const on = (handler: (payload: P) => void) => {
      if (isDev && log) {
        logger(`⚡ eventer → on: listener added to "${eventName}"`);
      }

      const listener = (e: Event) => {
        const { detail } = e as CustomEvent<P>;

        handler((detail ?? undefined) as P);
      };

      target.addEventListener(eventName, listener);

      return () => target.removeEventListener(eventName, listener);
    };

    return { emit, on };
  };
